import React, { useEffect } from 'react'
import Button from '@material-ui/core/Button'
import MenuItem from '@material-ui/core/MenuItem'
import FormikTextField from 'components/Fields/FormikTextField'
import { Form, Formik, FormikHelpers } from 'formik'
import * as yup from 'yup'
import Alert from '@material-ui/lab/Alert'
import { extractError, isFieldError } from 'utils/api'
import { useIntl } from 'react-intl'
import IntlMessages from '@crema/utility/IntlMessages'
import Box from '@material-ui/core/Box'
import { IApiState } from 'store/types'
import { apiStatus } from 'shared/constants/AppEnum'
import { IOfferingResponse, IOfferingTagResponse, ITagResponse } from 'types/api/temple'
import { useAppDispatch } from 'store/hooks'
import { offeringListThunk } from 'store/reducers/temple/offeringSlice'
import OfferingCheckboxList from './OfferingCheckboxList'

export type OfferingTagFormValues = {
    offering: string | number
    offerings: (string | number)[]
    tag: string
    name?: string
}

type FormSubmitHandleType = ((values: OfferingTagFormValues, formikHelpers: FormikHelpers<OfferingTagFormValues>) => void | Promise<any>) & Function

interface FormProps {
    buttonLabelId: string
    initialValues: OfferingTagFormValues
    successCb: (() => string) & Function
    onSubmitCb: FormSubmitHandleType
    api: IApiState
    isSuccess: boolean
    offerings: IOfferingResponse[]
    availableTags: ITagResponse[]
    isBulkAssign?: boolean
    offeringTags?: IOfferingTagResponse[]
    disabledExcludeTagIds?: (string | number)[]
    isEdit?: boolean
}

const FormOfferingTag: React.FC<FormProps> = (props) => {
    const {
        buttonLabelId,
        initialValues,
        successCb,
        onSubmitCb,
        api,
        isSuccess,
        offerings,
        availableTags = [],
        isBulkAssign,
        offeringTags = [],
        disabledExcludeTagIds = [],
        isEdit = false
    } = props
    const { messages } = useIntl()
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (offerings.length === 0) {
            dispatch(offeringListThunk({}))
        }
    }, [dispatch, offerings.length])

    const validationSchema = yup.object({
        offering: isBulkAssign ? yup.mixed() : yup.string().required('Offering is required'),
        offerings: isBulkAssign ? yup.array().min(1, 'Select at least one offering') : yup.array(),
        tag: yup.string().required('Tag is required'),
    })

    const getDisabledOfferingValues = (tagUrl: string) => {
        if (!tagUrl) return []
        return offeringTags
            .filter(tagRelation =>
                tagRelation.tag === tagUrl &&
                !disabledExcludeTagIds.includes(tagRelation.id)
            )
            .map(tagRelation => tagRelation.offering)
    }

    return (
        <>
            <Formik
                enableReinitialize
                validateOnChange={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmitCb}
            >
                {({ isSubmitting, dirty, values, setFieldValue, errors, touched }) => (
                    <Form noValidate autoComplete='off'>
                        <Box mb={4}>
                            {isBulkAssign ? (
                                <OfferingCheckboxList
                                    offerings={offerings}
                                    selectedOfferings={values.offerings}
                                    onChange={selectedOfferings => setFieldValue('offerings', selectedOfferings)}
                                    disabledOfferingValues={getDisabledOfferingValues(values.tag)}
                                    errorText={touched.offerings && errors.offerings ? String(errors.offerings) : ''}
                                />
                            ) : (
                                <FormikTextField
                                    select
                                    placeholder={messages['offeringTag.offering']}
                                    name='offering'
                                    label={<IntlMessages id='offeringTag.offering' />}
                                    variant='outlined'
                                    fullWidth
                                >
                                    {offerings.map(offering => (
                                        <MenuItem key={offering.id} value={offering.url}>
                                            {offering.name}
                                        </MenuItem>
                                    ))}
                                </FormikTextField>
                            )}
                            <FormikTextField
                                select
                                placeholder={messages['offeringTag.tag']}
                                name='tag'
                                label={<IntlMessages id='offeringTag.tag' />}
                                variant='outlined'
                                fullWidth
                                disabled={isEdit}
                            >
                                {availableTags.map(tag => (
                                    <MenuItem key={tag.url} value={tag.url}>
                                        {tag.name}
                                    </MenuItem>
                                ))}
                            </FormikTextField>
                        </Box>
                        <Button disabled={!dirty || isSubmitting} variant="contained" color="primary" type="submit">
                            <IntlMessages id={buttonLabelId} />
                        </Button>
                    </Form>
                )}
            </Formik>
            <Box marginTop={4}>
                {isSuccess && <Alert severity="success">{successCb()}</Alert>}
                {api.status === apiStatus.FAILED && !isFieldError(api.error) && (
                    <Alert severity="error">{extractError(api.error)}</Alert>
                )}
            </Box>
        </>
    )
}

export default FormOfferingTag
